import {
  useAcceptInvitationMutation,
  useConfirmEmailByInvitationMutation,
} from '#mutation';
import { AuthWorkspace } from '#types';
import {
  getSessionItem,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  setInLocalStorage,
} from '#utils';

export const useHandleWorkspaceInvitation = () => {
  const {
    mutateAsync: confirmEmailByInvitationLinkAsync,
    isPending: isConfirmingEmailByInvitationLink,
  } = useConfirmEmailByInvitationMutation({});

  const {
    mutateAsync: acceptInvitationAsync,
    isPending: isAcceptingInvitation,
  } = useAcceptInvitationMutation({});

  // return true if successfully confirm email by invitation link and accept invitation, false otherwise
  const handleWorkspaceInvitation = async ({
    userId,
    isThirdPartySignup,
    onSuccess,
    onError,
  }: {
    userId?: string;
    isThirdPartySignup: boolean;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }) => {
    const invitationToken = getSessionItem<string>(
      SESSION_STORAGE_KEYS.INVITATION_TOKEN,
    );

    const workspace = getSessionItem<AuthWorkspace>(
      SESSION_STORAGE_KEYS.WORKSPACE,
    );

    const workspaceId = workspace?.id;

    if (!invitationToken || !workspaceId) {
      return false;
    }

    try {
      if (!isThirdPartySignup) {
        await confirmEmailByInvitationLinkAsync({
          userid: userId,
          token: invitationToken,
        });
      }

      const acceptInvitationResponse = await acceptInvitationAsync({
        userid: userId,
        workspaceId: workspaceId.toString(),
      });

      if (acceptInvitationResponse?.status === 'OK') {
        const workspaceName = workspace?.name;

        if (workspaceName) {
          setInLocalStorage(
            LOCAL_STORAGE_KEYS.POST_AUTH_NOTIFICATION_MSG,
            `You have successfully joined the ${workspaceName} team!`,
          );
        }

        if (onSuccess) {
          onSuccess();
        }

        return true;
      }
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }

      return false;
    }

    return false;
  };

  const handleAcceptInvitation = async ({ userId }: { userId: string }) => {
    const workspace = getSessionItem<AuthWorkspace>(
      SESSION_STORAGE_KEYS.WORKSPACE,
    );
    const hasInvitationToken = !!getSessionItem(
      SESSION_STORAGE_KEYS.INVITATION_TOKEN,
    );

    if (hasInvitationToken && workspace?.id && userId) {
      try {
        // Todo(hao): clear invitation token after accepting invitation since it's used in multiple components.
        // we might calling /accept_invitation several times
        const acceptInvitationResponse = await acceptInvitationAsync({
          userid: userId,
          workspaceId: workspace.id.toString(),
        });

        if (acceptInvitationResponse?.status === 'OK' && workspace.name) {
          setInLocalStorage(
            LOCAL_STORAGE_KEYS.POST_AUTH_NOTIFICATION_MSG,
            `You have successfully joined the ${workspace.name} team!`,
          );
        }
      } catch (_) {}
    }
  };

  return {
    handleWorkspaceInvitation,
    handleAcceptInvitation,
    acceptInvitationAsync,
    isLoading: isConfirmingEmailByInvitationLink || isAcceptingInvitation,
  };
};
